import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SECTIONS } from "@/lib/sections";
import { useActiveSection } from "@/hooks/useActiveSection";

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const prefersReduced =
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  el.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "start" });
};

const ScrollIndicator: React.FC = () => {
  const active = useActiveSection();
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  return (
    <div
      className="hidden md:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-0"
      aria-label="Section navigation"
      role="navigation"
    >
      {SECTIONS.map((section, index) => {
        const isActive = active === section.id;
        const isHovered = hoveredId === section.id;
        const showLabel = isActive || isHovered;
        const color = section.color;

        return (
          <React.Fragment key={section.id}>
            {/* Connector line above (skip first) */}
            {index > 0 && (
              <div
                className="w-px flex-shrink-0"
                style={{
                  height: "24px",
                  background:
                    isActive
                      ? `linear-gradient(to bottom, ${color}40, ${color}20)`
                      : "",
                  transition: "background 0.4s ease",
                }}
              >
                {!isActive && (
                  <div className="w-full h-full bg-foreground/20" />
                )}
              </div>
            )}

            {/* Dot + label row */}
            <div
              className="relative flex items-center cursor-pointer"
              onMouseEnter={() => setHoveredId(section.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => scrollToSection(section.id)}
              role="button"
              tabIndex={0}
              aria-label={t(section.labelKey, { defaultValue: section.id })}
              onKeyDown={(e) => e.key === "Enter" && scrollToSection(section.id)}
            >
              {/* Label pill (left of dot) */}
              <AnimatePresence>
                {showLabel && (
                  <motion.div
                    key="label"
                    initial={{ opacity: 0, x: 8, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 6, scale: 0.9 }}
                    transition={{ duration: prefersReduced ? 0 : 0.2, ease: "easeOut" }}
                    className="absolute right-5 pr-3 mr-1 whitespace-nowrap"
                  >
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-md shadow-lg border ${!isActive ? 'bg-background/80 border-foreground/10 text-foreground/70' : ''}`}
                      style={{
                        ...(isActive ? {
                          background: `${color}22`,
                          borderColor: `${color}44`,
                          color: color,
                          boxShadow: `0 0 12px ${color}33`
                        } : {})
                      }}
                    >
                      {t(section.labelKey, { defaultValue: section.id })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Dot */}
              <div className="relative flex items-center justify-center w-5 h-5">
                {/* Active halo ring */}
                <AnimatePresence>
                  {isActive && !prefersReduced && (
                    <motion.div
                      key="halo"
                      className="absolute rounded-full"
                      style={{ borderColor: `${color}55`, border: "1px solid" }}
                      initial={{ width: 10, height: 10, opacity: 0.8 }}
                      animate={{ width: 20, height: 20, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
                    />
                  )}
                </AnimatePresence>

                {/* Main dot */}
                <motion.div
                  className={`rounded-full flex-shrink-0 ${!isActive ? 'bg-foreground' : ''}`}
                  animate={{
                    width: isActive ? 10 : 5,
                    height: isActive ? 10 : 5,
                    opacity: isActive ? 1 : isHovered ? 0.7 : 0.3,
                    scale: isHovered && !isActive ? 1.4 : 1,
                    backgroundColor: isActive ? color : undefined,
                    boxShadow: isActive
                      ? `0 0 8px ${color}cc, 0 0 20px ${color}66`
                      : "none",
                  }}
                  transition={
                    prefersReduced
                      ? { duration: 0 }
                      : { duration: 0.35, ease: "easeOut" }
                  }
                  style={isActive ? { backgroundColor: color } : {}}
                />
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div >
  );
};

export default ScrollIndicator;
